import { motion } from "framer-motion";

/**
 * MNH — Mohammed Naser Al Hammadi Contracting Co.
 * Premium architectural monogram:
 *  - Obsidian frame with gold inner border
 *  - Skyline / peaks roofline (representing scale and construction)
 *  - Bold MNH wordmark in desert gold
 *  - Foundation base-line with corner accents
 */
export const Logo = ({ size = 44, withText = true, light = false }) => {
  const gold = "#C8A951";
  const ink = "#0A0A0A";

  return (
    <div className="flex items-center gap-3" data-testid="brand-logo">
      <motion.svg
        width={size}
        height={size}
        viewBox="0 0 72 72"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="shrink-0"
      >
        {/* Obsidian backplate */}
        <rect x="0" y="0" width="72" height="72" rx="2" fill={ink} />

        {/* Subtle gold inner border */}
        <motion.rect
          x="4"
          y="4"
          width="64"
          height="64"
          rx="1"
          stroke={gold}
          strokeWidth="0.6"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.85 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        />

        {/* Skyline / peaks above the wordmark */}
        <motion.path
          d="M14 22 L22 14 L30 22 L38 14 L46 22 L54 14 L58 22"
          stroke={gold}
          strokeWidth="1.4"
          strokeLinecap="square"
          strokeLinejoin="miter"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.3, delay: 0.25, ease: "easeInOut" }}
        />

        {/* MNH wordmark — strong architectural feel */}
        <motion.text
          x="36"
          y="51"
          textAnchor="middle"
          fontFamily="Outfit, sans-serif"
          fontWeight="900"
          fontSize="22"
          fill={gold}
          letterSpacing="-0.5"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          MNH
        </motion.text>

        {/* Foundation baseline with corner accents */}
        <motion.line
          x1="18"
          y1="58"
          x2="54"
          y2="58"
          stroke={gold}
          strokeWidth="0.6"
          opacity="0.7"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, delay: 1.0 }}
        />
        <motion.circle
          cx="14"
          cy="58"
          r="1.1"
          fill={gold}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.2, duration: 0.4 }}
        />
        <motion.circle
          cx="58"
          cy="58"
          r="1.1"
          fill={gold}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.25, duration: 0.4 }}
        />

        {/* Tiny corner gold dot — refinement accent */}
        <circle cx="65" cy="7" r="1" fill={gold} opacity="0.85" />
      </motion.svg>

      {withText && (
        <div className="leading-tight">
          <div
            className={`text-[15px] font-black tracking-[0.22em] ${
              light ? "text-[#FCFBF7]" : "text-foreground"
            }`}
          >
            MNH
          </div>
          <div
            className={`text-[10px] tracking-[0.22em] uppercase font-medium ${
              light ? "text-[#FCFBF7]/70" : "text-muted-foreground"
            }`}
          >
            Contracting Co.
          </div>
        </div>
      )}
    </div>
  );
};
